import { forceCopyProperties } from "@amcharts/amcharts4/.internal/core/utils/Object";
import { BrandMap } from "../../../../Models/BrandMap";
import { CategoryMap } from "../../../../Models/CategoryMap";
import { IBrandCategory } from "../../../../Models/IBrandCategory";
import {
    IChartMergedDatapoint,
    IChartCompare,
    IChartGenericDatapoint,
    IStoreScoreRecord,
    IProfileRecord,
    IGPSChartMergedDatapoint,
    IBrand,
} from "../../../../Models/Models";
import { createGenericIcon } from "../../../../Components/Common/GenericIcon/GenericIcon.Helpers";
import Moment from "moment";

export const mergeRetailMatchData = (
    data: IChartCompare
): IChartMergedDatapoint[] => {
    return (data.selection1 as IChartGenericDatapoint[])?.map(
        (item: IChartGenericDatapoint, i: number) => {
            return {
                id: item.id,
                nationalWeight: data.national
                    ? data.national.find(
                        (nat: IChartGenericDatapoint) => nat.id === item.id
                    )?.weight
                    : null,
                selection1PrimaryWeight: item.weight,
                selection2PrimaryWeight: data.selection2
                    ? (data.selection2 as IChartGenericDatapoint[]).find(
                        (selection2: IChartGenericDatapoint) => selection2.id === item.id
                    )?.weight
                    : null,
            };
        }
    );
};

export const mergeSegRetailMatchData = (
    data: IChartCompare
): IChartMergedDatapoint[] => {
    return (data.selection1 as IProfileRecord[]).map(
        (item: IProfileRecord, i: number) => {
            return {
                id: item.seg,
                selection1PrimaryWeight: item.pct,
            };
        }
    );
};

export const mergeWeeklyData = (
    data: IChartCompare
): IChartMergedDatapoint[] => {
    const selectionOne: IGPSChartMergedDatapoint[]
    = (data.selection1 as Array<IGPSChartMergedDatapoint>)?.length > 0
        ? Array.from(data.selection1 as Array<IGPSChartMergedDatapoint>)
        : [];

    const selectionTwo: IGPSChartMergedDatapoint[]
    = (data.selection2 as Array<IGPSChartMergedDatapoint>)?.length > 0
        ? Array.from(data.selection2 as Array<IGPSChartMergedDatapoint>)
        : [];

    const nationalDataMap: Map<string, number>
    = (data.selection2 as Array<IGPSChartMergedDatapoint>)?.length > 0
        ? new Map(Array.from(data.national, (n) => [n.id, n.weight]))
        : new Map();

    const dataMap: Map<string, IChartMergedDatapoint> = new Map();

    for (const selection of selectionOne) {
        const { id, visitWeight, visitorWeight, weight } = selection;
        dataMap.set(selection.id, {
            id: selection.id,
            nationalWeight: nationalDataMap.has(id) ? nationalDataMap.get(id) : 0,
            selection1PrimaryWeight: visitorWeight
                ? visitorWeight
                : weight
                    ? weight
                    : 0,
            selection1SecondaryWeight: visitWeight ? visitWeight : 0,
            selection2PrimaryWeight: 0,
            selection2SecondaryWeight: 0,
        });
    }

    if (selectionTwo?.length > 0) {
        for (const selection of selectionTwo) {
            const { id, visitWeight, visitorWeight, weight } = selection;

            const s = dataMap.get(selection.id);
            const selection2PrimaryWeight = visitorWeight
                ? visitorWeight
                : weight
                    ? weight
                    : 0;
            const selection2SecondaryWeight = visitWeight;

            if (s) {
                s.selection2PrimaryWeight
          = selection2PrimaryWeight >= 0 ? selection2PrimaryWeight : 0;
                s.selection2SecondaryWeight
          = selection2SecondaryWeight >= 0 ? selection2SecondaryWeight : 0;
            } else {
                dataMap.set(id, {
                    id: id,
                    nationalWeight: nationalDataMap.has(id) ? nationalDataMap.get(id) : 0,
                    selection1PrimaryWeight: 0,
                    selection1SecondaryWeight: 0,
                    selection2PrimaryWeight:
            selection2PrimaryWeight >= 0 ? selection2PrimaryWeight : 0,
                    selection2SecondaryWeight:
            selection2SecondaryWeight >= 0 ? selection2SecondaryWeight : 0,
                });
            }
        }
    }

    const arrayifiedDataMap = Array.from(dataMap.values());

    /* BUG 14789 */
    return arrayifiedDataMap.sort((a: IChartMergedDatapoint, b: IChartMergedDatapoint) => {
        const firstDate = a.id;// a.id, b.id example: "2020-01-27T00:00:00"
        const secondDate = b.id;

        return Moment(firstDate).diff(Moment(secondDate));// Moment.diff(Moment) returns the difference in milliseconds
    });
};

export const mergeGPSData = (data: IChartCompare): IChartMergedDatapoint[] => {
    const formattedData: IChartMergedDatapoint[] = [];
    const selectionOne = Array.from(
    data.selection1 as Array<IGPSChartMergedDatapoint>
    );
    const selectionTwoMap: Map<string, IGPSChartMergedDatapoint> = new Map();

    (data.selection2 as Array<IGPSChartMergedDatapoint>)?.forEach((s) =>
        selectionTwoMap.set(s.id, s)
    );

    for (const selection of selectionOne) {
        const selection2Obj: IGPSChartMergedDatapoint = selectionTwoMap.get(
            selection.id
        );
        let selection2PrimaryWeight = 0;
        let selection2SecondaryWeight = 0;

        if (selection2Obj) {
            selection2PrimaryWeight = selection2Obj.visitorWeight
                ? selection2Obj.visitorWeight
                : selection2Obj.weight;
            selection2SecondaryWeight = selection2Obj.visitWeight;
        }

        formattedData.push({
            id: selection.id,
            nationalWeight: data.national
                ? data.national.find((nat) => nat.id === selection.id).weight
                : null,
            selection1PrimaryWeight: selection.visitorWeight
                ? selection.visitorWeight
                : selection.weight,
            selection1SecondaryWeight: selection.visitWeight,
            selection2PrimaryWeight: selection2PrimaryWeight,
            selection2SecondaryWeight: selection2SecondaryWeight,
        });
    }
    return formattedData;
};

export const reverseGeocode = (
    lat: number,
    lng: number
): Promise<google.maps.GeocoderResult[]> => {
    const geocoder: google.maps.Geocoder = new google.maps.Geocoder();

    return new Promise((resolve, reject) => {
        geocoder.geocode(
            { location: { lat, lng } },
            (results: google.maps.GeocoderResult[]) => {
                if (results && results[0]) {
                    resolve(results);
                } else {
                    reject();
                }
            }
        );
    });
};

export const getBenchmarkShortAddress = async (
    benchmark: IStoreScoreRecord
): Promise<string> => {
    const { lat, lon: lng }: IStoreScoreRecord = benchmark;
    const geocoder: google.maps.Geocoder = new google.maps.Geocoder();

    return new Promise((resolve, reject) => {
        reverseGeocode(lat, lng)
            .then((results) => {
                const shortAddress: string = formatShortAddressFromGeocoder(results);
                resolve(shortAddress);
            })
            .catch((error) => {
                reject();
            });
    });
};

export const getBenchmarkLongAddress = (
    benchmark: IStoreScoreRecord
): Promise<string> => {
    const { lat, lon: lng }: IStoreScoreRecord = benchmark;
    const geocoder: google.maps.Geocoder = new google.maps.Geocoder();

    return new Promise((resolve, reject) => {
        reverseGeocode(lat, lng)
            .then((results) => {
                const longAddress = formatLongAddressFromGeocoder(results);
                resolve(longAddress);
            })
            .catch((error) => {
                reject();
            });
    });
};

const getAddressComponentOfType = (
    addressComponents: google.maps.GeocoderAddressComponent[],
    type: string
): google.maps.GeocoderAddressComponent => {
    return addressComponents.find((c) => {
        return c.types.find((t) => t === type);
    });
};

export const formatShortAddressFromGeocoder = (
    results: google.maps.GeocoderResult[]
): string => {
    const addressComponents: google.maps.GeocoderAddressComponent[]
    = results[0].address_components;

    const streetNumberComponent = getAddressComponentOfType(
        addressComponents,
        "street_number"
    );
    const streetNameComponent = getAddressComponentOfType(
        addressComponents,
        "route"
    );

    return addressComponents
        ? `${
            streetNumberComponent?.long_name
        } ${streetNameComponent?.long_name.toUpperCase()}`
        : "";
};

export const formatLongAddressFromGeocoder = (
    results: google.maps.GeocoderResult[]
): string => {
    const addressComponents: google.maps.GeocoderAddressComponent[]
    = results[0].address_components;

    const streetNumberComponent = getAddressComponentOfType(
        addressComponents,
        "street_number"
    );
    const streetNameComponent = getAddressComponentOfType(
        addressComponents,
        "route"
    );
    const cityComponent = getAddressComponentOfType(
        addressComponents,
        "locality"
    );
    const stateComponent = getAddressComponentOfType(
        addressComponents,
        "administrative_area_level_1"
    );
    const zipcodeComponent = getAddressComponentOfType(
        addressComponents,
        "postal_code"
    );

    return addressComponents
        ? `${streetNumberComponent?.long_name} ${streetNameComponent?.long_name}, ${cityComponent?.long_name}, ${stateComponent?.long_name} ${zipcodeComponent?.long_name}`
        : "";
};

export const getCityFromGeocoder = (
    results: google.maps.GeocoderResult[]
): string => {
    const addr: google.maps.GeocoderAddressComponent[]
    = results[0].address_components;
    const probableCityComponent = addr.find((component) => {
        const isCity = component.types.indexOf("locality") > -1;
        return isCity;
    });

    return probableCityComponent?.long_name || null;
};

export function formatLongAddressFromRecord(record: IStoreScoreRecord): string {
    const addr: string = record && record.addr;
    const { city, st, zip } = record;

    return addr ? `${addr}, ${city}, ${st} ${zip}` : "";
}

/**
 * Converts the first letter of each word in a string with spaces to an uppercase.
 * @param string
 */
export const titleCase = (string: string): string => {
    if (!string) {
        return "";
    }
    const splitStr = string.toLowerCase().split(" ");

    const substringStartsWithPeriod = (substring: string): boolean =>
        substring.charCodeAt(0) == 46;

    for (let i = 0; i < splitStr.length; i++) {
    // You do not need to check if i is larger than splitStr length, as your for does that for you
    // Assign it back to the array
        const first = splitStr[i].charAt(0).toUpperCase();
        let sub = splitStr[i].substring(1);

        if (substringStartsWithPeriod(sub)) {
            sub = sub.charAt(0) + sub.charAt(1).toUpperCase() + sub.substring(2);
        }

        splitStr[i] = first + sub;
    }
    // Directly return the joined string
    return splitStr.join(" ");
};

export const testImageOnloadAsync = (src: string): Promise<boolean> => {
    const imagePromise: Promise<boolean> = new Promise((resolve) => {
        if (!src || src === "") {
            resolve(false);
        } else {
            const testImage = new Image();

            testImage.addEventListener("load", () => {
                resolve(true);
            });

            testImage.addEventListener("error", () => {
                resolve(false);
            });

            testImage.src = src;
        }
    });

    return imagePromise;
};

export const isClearbitLogoPath = (path: string): boolean => {
    return path.includes('clearbit');
};

export const getValidLogoPath = async (originalPath: string, brandName: string): Promise<string> => {
    let transformedPath: string;
    const validSrc = await testImageOnloadAsync(originalPath);
    if (!validSrc) {
        transformedPath = await createGenericIcon(brandName);
    } else {
        transformedPath = originalPath;
    }
    return transformedPath;
};

export const generateMobilyticsPlaceLogo = (
    logoPath: string
): Promise<string> => {
    return new Promise((resolveURL: (path: string) => void) => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        canvas.width = 60;
        canvas.height = 60;

        const logoOnload = () => {
            return new Promise((resolveLogo: (loaded: boolean) => void) => {
                const logo = new Image();
                logo.crossOrigin = "anonymous";
                logo.src = logoPath;

                logo.addEventListener("load", function () {
                    context.drawImage(logo, 11, 11, 38, 38);
                    resolveLogo(true);
                });

                logo.addEventListener("error", () => {
                    resolveLogo(false);
                });
            });
        };

        logoOnload().then(() => {
            const poly = new Image();

            poly.src = "/Images/NEW_UI/left_nav/buxton_data/Polygon_Icon.svg";

            poly.addEventListener("load", function () {
                context.drawImage(poly, 32, 0);

                resolveURL(canvas.toDataURL());
            });
        });
    });
};

export const createBrandMap = (brands: IBrand[], mapKey: string): BrandMap => {
    const brandMap: BrandMap = new Map();

    for (const brand of brands) {
        if (!brandMap.has(brand[mapKey])) {
            const b = new Map();

            brandMap.set(brand[mapKey], b.set(brand.brandName, brand));
        } else {
            brandMap.get(brand[mapKey]).set(brand.brandName, brand);
        }
    }

    return brandMap;
};

export const createCategoryMap = (
    categories: IBrandCategory[],
    brandMap: BrandMap,
    mapKey: string
): CategoryMap => {
    const categoryMap: CategoryMap = new Map();

    for (const category of categories) {
        if (!categoryMap.get(category[mapKey])) {
            categoryMap.set(category[mapKey], {
                categoryName: category.categoryName,
                subCategories: [],
            });

            if (brandMap.has(category.naicsCode)) {
                categoryMap.get(category[mapKey]).subCategories.push({
                    categoryName: category.subcategoryName,
                    naicsCode: category.naicsCode,
                    brands: brandMap.get(category.naicsCode),
                });
            }
        } else if (brandMap.has(category.naicsCode)) {
            categoryMap.get(category[mapKey]).subCategories.push({
                categoryName: category.subcategoryName,
                naicsCode: category.naicsCode,
                brands: brandMap.get(category.naicsCode),
            });
        }
    }

    //if there are no brands on the category (because there were no matching brands in the map) add them manually

    return categoryMap;
};
